import React, { useCallback, useRef, useEffect, useState } from "react";

const VideoTestimonial = ({ videos = [] }) => {
  const scrollRef = useRef(null);
  // Changed to false so sound is on by default
  const [isMuted, setIsMuted] = useState(false); 

  useEffect(() => {
    const observerOptions = {
      root: null,
      threshold: 0.6,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector("video");
        if (video) {
          if (entry.isIntersecting) {
            // Attempt to play with sound
            const playPromise = video.play();

            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                // Autoplay with sound was blocked
                console.warn("Autoplay with sound blocked. Muting to allow playback.");
                video.muted = true;
                setIsMuted(true);
                video.play();
              });
            }
          } else {
            video.pause();
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    const cards = document.querySelectorAll(".dark-glass-card");
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [videos]);

  const toggleMute = () => setIsMuted((prev) => !prev);

  // Rest of your logic (scrollLeft, scrollRight, etc.)
  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });
  }, []);

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });
  }, []);

  if (!videos?.length) return <EmptyState />;
  const isSingle = videos.length === 1;

  return (
    <section className="black-orange-wrapper">
      <div className="glow-fill"></div>
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>
      
      <div className="glass-content-overlay">
        <header className="testimonial-header">
          <div className="glass-badge">Success Stories</div>
        </header>

        <div ref={scrollRef} className={`video-gallery ${isSingle ? "centered" : ""}`}>
          {videos.map((item, index) => (
            <article key={item.id || index} className="dark-glass-card">
              <div className="video-perspective">
                <div className="video-frame">
                  <div className="frame-header">
                    <div className="dots"><span></span><span></span><span></span></div>
                    <div className="live-rec">● TESTIMONIAL</div>
                  </div>
                  
                  <div className="video-container">
                    <video
                      src={item.videoUrl}
                      className="main-video"
                      loop
                      muted={isMuted} 
                      playsInline
                      preload="auto"
                    />
                    <button className="audio-control" onClick={toggleMute}>
                      {isMuted ? "🔇" : "🔊"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="content-panel">
                <div className="star-rating">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} className={`star ${i < item.rating ? "active" : ""}`}>★</span>
                  ))}
                </div>
                <p className="feedback-text">{item.content}</p>
                <footer className="author-info">
                  <div className="accent-divider"></div>
                  <h4 className="author-name">{item.name}</h4>
                  <p className="author-role">{item.role}</p>
                </footer>
              </div>
            </article>
          ))}
        </div>

        {!isSingle && (
          <div className="navigation-controls">
            <button className="nav-arrow-dark" onClick={scrollLeft}>←</button>
            <div className="track-dark">
              <div className="fill-orange"></div>
            </div>
            <button className="nav-arrow-dark" onClick={scrollRight}>→</button>
          </div>
        )}
      </div>

      <BlackOrangeStyles />
    </section>
  );
};

// ... BlackOrangeStyles and EmptyState remain the same
const EmptyState = () => (
  <div className="empty-state-dark">
    <p>Waiting for the spotlight...</p>
    <BlackOrangeStyles />
  </div>
);

const BlackOrangeStyles = () => (
  <style jsx>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;800&display=swap');

    .black-orange-wrapper {
      --primary-orange: #f97316;
      --deep-black: #080808;
      --card-bg: rgba(15, 15, 15, 0.75);
      --card-border: rgba(249, 115, 22, 0.25);
      --radius: 12px;
      position: relative;
      width: 100%;
      padding: 40px 0;
      border-radius: 12px;
      background: var(--deep-black);
      font-family: 'Plus Jakarta Sans', sans-serif;
      overflow: hidden;
    }

    .glow-fill {
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at 40% 40%, rgba(249, 115, 22, 0.12) 0%, transparent 60%);
      z-index: 0;
    }

    .orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(100px);
      z-index: 0;
      opacity: 0.3;
    }
    .orb-1 { width: 400px; height: 400px; background: #ea580c; top: -100px; left: -100px; }
    .orb-2 { width: 450px; height: 450px; background: #9a3412; bottom: -150px; right: -100px; }

    .glass-content-overlay { position: relative; z-index: 1; }
    .testimonial-header { text-align: center; margin-bottom: 40px; }

    .glass-badge {
      display: inline-block;
      padding: 6px 14px;
      background: rgba(249, 115, 22, 0.15);
      border: 1px solid var(--card-border);
      color: var(--primary-orange);
      border-radius: var(--radius);
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
    }

    .video-gallery {
      display: flex;
      gap: 25px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      padding: 15px 5%;
      scrollbar-width: none;
    }
    .video-gallery::-webkit-scrollbar { display: none; }

    .dark-glass-card {
      min-width: 680px;
      display: flex;
      align-items: center;
      gap: 25px;
      padding: 20px;
      background: var(--card-bg);
      backdrop-filter: blur(20px);
      border: 1px solid var(--card-border);
      border-radius: var(--radius);
      scroll-snap-align: center;
    }

    .video-container {
      position: relative;
      width: 100%;
    }

    .audio-control {
      position: absolute;
      bottom: 12px;
      right: 12px;
      background: rgba(0, 0, 0, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 14px;
      transition: transform 0.2s;
      z-index: 10;
    }

    .audio-control:hover {
      transform: scale(1.1);
      background: var(--primary-orange);
    }

    .video-perspective { flex: 1.1; }
    .video-frame {
      background: #000;
      border-radius: var(--radius);
      overflow: hidden;
      border: 1px solid rgba(255,255,255,0.05);
    }

    .frame-header {
      background: #121212;
      padding: 8px 12px;
      display: flex;
      justify-content: space-between;
    }

    .dots span { width: 6px; height: 6px; border-radius: 50%; background: #2a2a2a; margin-right: 4px; display: inline-block; }
    .dots span:first-child { background: var(--primary-orange); }
    .live-rec { font-size: 8px; color: var(--primary-orange); font-weight: 700; }

    .main-video {
      width: 100%;
      aspect-ratio: 1 / 1; 
      object-fit: cover;
      display: block;
    }

    .content-panel { flex: 1; padding: 10px; position: relative; }
    .quote-icon { font-size: 80px; position: absolute; top: -20px; left: 0; opacity: 0.2; color: var(--primary-orange); }
    .star-rating { margin-bottom: 12px; }
    .star { font-size: 15px; color: #1e293b; margin-right: 2px; }
    .star.active { color: var(--primary-orange); filter: drop-shadow(0 0 5px rgba(249, 115, 22, 0.5)); }
    .feedback-text { font-size: 15px; line-height: 1.6; color: #e2e8f0; margin-bottom: 20px; }
    .accent-divider { width: 35px; height: 3px; background: var(--primary-orange); margin-bottom: 12px; }
    .author-name { font-size: 18px; font-weight: 800; color: #ffffff; margin: 0; }
    .author-role { font-size: 12px; color: var(--primary-orange); margin-top: 2px; font-weight: 600; }

    .navigation-controls {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;
      margin-top: 35px;
    }

    .nav-arrow-dark {
      width: 44px;
      height: 44px;
      background: rgba(20, 20, 20, 0.8);
      border: 1px solid var(--card-border);
      border-radius: var(--radius);
      font-size: 18px;
      color: var(--primary-orange);
      cursor: pointer;
    }

    .track-dark { width: 100px; height: 2px; background: #1a1a1a; border-radius: 10px; }
    .fill-orange { width: 30%; height: 100%; background: var(--primary-orange); }

    @media (max-width: 850px) {
      .dark-glass-card { min-width: 85vw; flex-direction: column; }
      .main-video { aspect-ratio: 16/9; }
    }
  `}</style>
);

export default VideoTestimonial;